import React, { useMemo } from 'react'
import { NavigationButton, SettingsControlTile } from '../common'
import StyledSwitch from '@e3dc-react/shell/Elements/Switch/StyledSwitch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFormContext, useWatch } from 'react-hook-form'
import { Ai360SettingsFormValues } from '../../types'
import { FeedInTariffType } from 'src/Libs/dynamic-power-prices/types'

interface Props {
  onClick?: () => void
  containerClassName?: string
}

/**
 * component to activate/deactivate PV feature and navigate to its settings
 */
const PVSettingsControlTile: React.FC<Props> = ({ onClick, containerClassName }) => {
  const { t } = useCustomTranslation()
  const { control, setValue } = useFormContext<Ai360SettingsFormValues>()
  const isAI360Active = useWatch({ control, name: 'isAI360Active' })
  const isChargingFromPVActive = useWatch({ control, name: 'isChargingFromPVActive' })
  const feedInTariffType = useWatch({ control, name: 'pvSettings.feedInTariffType' })
  const feedInTariff = useWatch({ control, name: 'pvSettings.feedInTariff' })

  const description = useMemo(() => {
    if (!isChargingFromPVActive) {
      return undefined
    }

    if (isAI360Active && isChargingFromPVActive && (feedInTariffType === '' || feedInTariff === '')) {
      return t('ai360.settings-page.pv-settings.incomplete-configuration')
    }

    if (feedInTariffType === FeedInTariffType.Fixed) {
      return t(`ai360.settings-page.pv-settings.feed-in-tariff-type.${feedInTariffType}`)
    }

    if (feedInTariffType === FeedInTariffType.ZeroWhenNegativePrices) {
      return t(`ai360.settings-page.pv-settings.feed-in-tariff-type.${feedInTariffType}`)
    }

    return undefined
  }, [isChargingFromPVActive, isAI360Active, feedInTariffType, feedInTariff, t])

  const handleToggle = (): void => {
    const newValue = !isChargingFromPVActive
    setValue('isChargingFromPVActive', newValue, { shouldDirty: true })
  }

  const handleNavigation = (): void => {
    onClick?.()
  }

  const prefix = <StyledSwitch checked={isChargingFromPVActive} onChange={handleToggle} disabled={!isAI360Active} />
  const suffix = <NavigationButton onClick={handleNavigation} />

  return (
    <SettingsControlTile
      title={t('ai360.settings-page.charging-from-pv.title')}
      description={description}
      prefix={prefix}
      suffix={suffix}
      containerClassName={containerClassName}
    />
  )
}

export default PVSettingsControlTile
